import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaDownload,
  FaFileAlt,
  FaClipboardList,
  FaUserTie,
  FaHandshake,
  FaShieldAlt,
  FaFileContract,
  FaUsers,
  FaFilePowerpoint
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const Onboarding = ({ embedded = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleBack = () => {
    navigate('/portal/hr/materials');
  };

  const handleDownload = (documentName) => {
    // Find the document in our fetched documents
    const doc = documents.find(d => d.title === documentName);
    if (doc && doc.fileUrl) {
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = doc.fileUrl;
      link.download = doc.fileName || doc.title;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.log(`No file available for ${documentName}`);
      // In a real implementation, you might want to show an alert or notification
    }
  };

  // Fetch documents from Firebase
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'templatesCollections'));
        const docs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docs);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const internalResources = [
    {
      name: 'New Hire Checklist',
      description: 'Comprehensive checklist for managers to ensure all onboarding steps are completed',
      icon: <FaClipboardList className="h-6 w-6" />
    },
    {
      name: 'New Hire Onboarding',
      description: 'Step-by-step guide for conducting new employee orientation sessions',
      icon: <FaUserTie className="h-6 w-6" />
    }
  ];

  const newHireMaterials = [
    {
      name: 'Direct Deposit',
      description: 'Form for new employees to set up automatic payroll deposits',
      icon: <FaFileAlt className="h-6 w-6" />
    },
    {
      name: 'New Hire Presentation',
      description: 'Company overview and policies presentation for orientation',
      icon: <FaFilePowerpoint className="h-6 w-6" />
    },
    {
      name: 'Employee Referral Program',
      description: 'Details about the company\'s employee referral bonus program',
      icon: <FaUsers className="h-6 w-6" />
    },
    {
      name: 'Drug Test Consent',
      description: 'Required consent form for pre-employment drug screening',
      icon: <FaShieldAlt className="h-6 w-6" />
    },
    {
      name: 'Non Compete',
      description: 'Standard non-compete agreement for new employees',
      icon: <FaFileContract className="h-6 w-6" />
    }
  ];

  // Helper function to check if a document has a file available
  const hasFileAvailable = (documentName) => {
    const doc = documents.find(d => d.title === documentName);
    return doc && doc.fileUrl;
  };

  if (loading) {
    return (
      <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
        {!embedded && <PortalBackground />}
        <div className="relative max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <BBALoader size={48} className="mx-auto mb-4" />
              <p className="text-lg">Loading onboarding resources...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        {!embedded && (
          <div className="flex items-center justify-between mb-8 relative">
            <div className="flex-1 flex items-center">
              <BackButton onClick={handleBack} />
            </div>
            <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
              <FaGraduationCap className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Onboarding Resources</h1>
            </div>
            <div className="flex-1 flex justify-end">
            </div>
          </div>
        )}

        {/* Internal Resources Section */}
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <FaClipboardList className={`text-2xl mr-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h2 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              Internal Resources
            </h2>
            <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${
              darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
            }`}>
              For Managers
            </span>
          </div>
          <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Essential documents and guides for managing the onboarding process internally.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {internalResources.map((resource, index) => (
              <div key={index} className={`p-6 rounded-lg shadow-md border ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } hover:shadow-lg transition-shadow duration-200`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg mr-4 ${
                      darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {resource.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {resource.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {resource.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handleDownload(resource.name)}
                    disabled={!hasFileAvailable(resource.name)}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasFileAvailable(resource.name)
                        ? darkMode 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                        : darkMode
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FaDownload className="inline mr-2" />
                    {hasFileAvailable(resource.name) ? 'Download' : 'File Not Available'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* New Hire Materials Section */}
        <div className="mb-8">
          <div className="flex items-center mb-6">
            <FaHandshake className={`text-2xl mr-3 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            <h2 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              New Hire Materials
            </h2>
            <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full ${
              darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
            }`}>
              For New Employees
            </span>
          </div>
          <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Required and optional documents to provide to new employees during the onboarding process.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newHireMaterials.map((material, index) => (
              <div key={index} className={`p-6 rounded-lg shadow-md border ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } hover:shadow-lg transition-shadow duration-200`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg mr-4 ${
                      darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-600'
                    }`}>
                      {material.icon}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {material.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {material.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => handleDownload(material.name)}
                    disabled={!hasFileAvailable(material.name)}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasFileAvailable(material.name)
                        ? darkMode 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                        : darkMode
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FaDownload className="inline mr-2" />
                    {hasFileAvailable(material.name) ? 'Download' : 'File Not Available'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding; 